import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { tournamentsApi } from '../../api/tournaments';
import { cardsApi } from '../../api/cards';
import { authApi } from '../../api/auth';
import { Search, Play } from 'lucide-react';
import FootballField from './FootballField';
import { pickNextMatchForTournamentWindow } from '../../utils/pickNextMatchForTournamentWindow';
import {
  collectFormationTeamCardIds,
  filterFormationCardsWithoutNa,
  filterWorldCupConvocatedCards,
  formatUpcomingMatchFirstLabel,
  isMissingUpcomingMatchFirst,
  isWorldCupTournament
} from '../../utils/matchCallUpUtils';
import '../../styles/tournament-entry-gaming.css';

const TournamentEntry = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user] = useState(authApi.getCurrentUser());
  const [tournament, setTournament] = useState(null);
  const [userCards, setUserCards] = useState([]);
  const [usedCardsInOtherTournaments, setUsedCardsInOtherTournaments] = useState([]); // Card già usate in altri tornei del gameweek
  const toBoolFlag = useCallback((value) => {
    if (value === true || value === 1 || value === '1') return true;
    if (typeof value === 'string') {
      const v = value.trim().toLowerCase();
      return v === 'true' || v === 'yes' || v === 'on';
    }
    return false;
  }, []);
  const [formation, setFormation] = useState('4-3-3'); // Formazione selezionata
  const [team, setTeam] = useState({
    goalkeeper: null,
    defenders: [], // Minimo 3, ma può essere di più fino a 11 totali in campo
    midfielders: [], // Minimo 3, ma può essere di più fino a 11 totali in campo
    attackers: [], // Minimo 1, ma può essere di più fino a 11 totali in campo
    reserves: { RES1: null, RES2: null, RES3: null }, // RES1 = difensore, RES2 = centrocampista, RES3 = attaccante (obbligatorie)
    legend: null
  });

  // Stessa logica di TournamentFormation: batch + next, next_2, in_progress, last (allineato a CardsService / team_cards).
  const fetchNextMatchForCards = useCallback(async (cards, currentTournament) => {
    if (!cards?.length) return [];
    if (!currentTournament || !currentTournament.start_date || !currentTournament.end_date) {
      return cards.map((card) => ({
        ...card,
        next_match: null,
        match_id: null,
        playsInRange: null,
        upcoming_match_first: null,
        upcoming_match_second: null
      }));
    }

    try {
      const templateIdByCardId = {};
      const rarityByCardId = {};
      const playerIdByCardId = {};
      cards.forEach((c) => {
        if (c?.id != null && c?.template_id != null) {
          templateIdByCardId[c.id] = c.template_id;
        }
        if (c?.id != null && c?.rarity?.name) {
          rarityByCardId[c.id] = c.rarity.name;
        }
        const playerId = c?.player?.id ?? c?.player_id ?? c?.player_id_from_players;
        if (c?.id != null && playerId != null && !Number.isNaN(Number(playerId))) {
          playerIdByCardId[c.id] = Number(playerId);
        }
      });
      const batchRes = await cardsApi.getNextMatchesBatch(
        cards.map((c) => c.id),
        Object.keys(templateIdByCardId).length ? templateIdByCardId : undefined,
        Object.keys(rarityByCardId).length ? rarityByCardId : undefined,
        Object.keys(playerIdByCardId).length ? playerIdByCardId : undefined
      );
      const matches = batchRes?.success ? batchRes.matches || {} : {};
      return cards.map((card) => {
        const m = matches[card.id];
        const picked = pickNextMatchForTournamentWindow(m, currentTournament.start_date, currentTournament.end_date);
        return {
          ...card,
          next_match: picked.next_match,
          match_id: picked.match_id,
          playsInRange: picked.playsInRange,
          upcoming_match_first: m?.next_match ?? null,
          upcoming_match_second: m?.next_match_2 ?? null
        };
      });
    } catch (error) {
      return cards.map((card) => ({
        ...card,
        next_match: null,
        match_id: null,
        playsInRange: null,
        upcoming_match_first: null,
        upcoming_match_second: null
      }));
    }
  }, []);
  
  // Formazioni valide compatibili con gli slot persistiti in team_cards:
  // max 5 DEF, max 5 MID, max 3 ATT.
  // Requisiti minimi: almeno 3 DEF, almeno 3 MID, almeno 1 ATT
  const allFormations = [
    '4-3-3', '4-4-2', '4-5-1', 
    '3-4-3', '3-5-2', 
    '5-3-2', '5-4-1',
    '5-2-3'
  ];
  
  const getFormationCounts = (form = formation) => {
    const parts = (form || formation).split('-').map(Number);
    let maxDef = 4, maxMid = 3, maxAtt = 3;
    if (parts.length === 3) {
      maxDef = parts[0]; maxMid = parts[1]; maxAtt = parts[2];
    } else if (parts.length === 4) {
      maxDef = parts[0]; maxMid = parts[1] + parts[2]; maxAtt = parts[3];
    } else if (parts.length === 5) {
      maxDef = parts[0]; maxMid = parts[1] + parts[2]; maxAtt = parts[3] + parts[4];
    }
    return { maxDef, maxMid, maxAtt };
  };

  // Filtra le formazioni che rispettano i requisiti minimi: almeno 3 DEF, almeno 3 MID, almeno 1 ATT
  const validFormations = allFormations.filter(form => {
    const parts = form.split('-').map(Number);
    let defenders = 0, midfielders = 0, attackers = 0;
    
    if (parts.length === 3) {
      defenders = parts[0];
      midfielders = parts[1];
      attackers = parts[2];
    } else if (parts.length === 4) {
      defenders = parts[0];
      midfielders = parts[1] + parts[2];
      attackers = parts[3];
    } else if (parts.length === 5) {
      defenders = parts[0];
      midfielders = parts[1] + parts[2];
      attackers = parts[3] + parts[4];
    }
    
    if (defenders > 5 || midfielders > 5 || attackers > 3) {
      return false;
    }

    // Verifica requisiti minimi: almeno 3 DEF, almeno 3 MID, almeno 1 ATT
    return defenders >= 3 && midfielders >= 3 && attackers >= 1;
  });
  const [filters, setFilters] = useState({
    search: '',
    rarity: '',
    position: '',
    nationality: '',
    team: ''
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null); // { role: 'GK'|'DEF'|'MID'|'ATT'|'RES1'|'RES2'|'RES3', index?: number }
  // Multi-select per linea (DEF/MID/ATT): selezione multipla fino al numero indicato dal modulo
  const [pendingLineSelection, setPendingLineSelection] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadTournament();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  // Le card verranno caricate solo quando si apre il modal

  const loadTournament = async () => {
    try {
      const response = await tournamentsApi.getById(parseInt(id));
      console.log('[TournamentEntry] loadTournament - Full API response:', response);
      if (response?.success) {
        console.log('[TournamentEntry] loadTournament - Tournament data:', response.tournament);
        console.log('[TournamentEntry] loadTournament - Tournament gameweek_id:', response.tournament?.gameweek_id);
        setTournament(response.tournament);
        
        // Carica le card già usate in altri tornei dello stesso gameweek
        if (response.tournament?.type === 'gameweek' && user?.id) {
          await loadUsedCardsInGameweek(response.tournament);
        }
      }
    } catch (error) {
      console.error('[TournamentEntry] Error loading tournament:', error);
    }
  };

  const loadUsedCardsInGameweek = async (currentTournament) => {
    try {
      console.log('[TournamentEntry] loadUsedCardsInGameweek - Tournament data:', currentTournament);
      console.log('[TournamentEntry] loadUsedCardsInGameweek - gameweek_id:', currentTournament?.gameweek_id);
      
      if (currentTournament?.type !== 'gameweek' || !user?.id) {
        console.log('[TournamentEntry] loadUsedCardsInGameweek - Skipping - not a gameweek tournament or no user');
        return [];
      }
      
      if (!currentTournament?.gameweek_id) {
        console.error('[TournamentEntry] loadUsedCardsInGameweek - ERROR: Tournament is gameweek type but gameweek_id is missing!', currentTournament);
        return [];
      }

      // Use gameweek_id as the unique identifier
      const gameweekId = currentTournament.gameweek_id;
      console.log(`[TournamentEntry] loadUsedCardsInGameweek - Loading used cards for gameweek ID: ${gameweekId}, tournament ID: ${currentTournament.id}`);
      
      // Use the API endpoint that queries directly by gameweek_id
      const response = await tournamentsApi.getUsedCardsInGameweek(user.id, gameweekId, currentTournament.id);
      
      if (response?.success && Array.isArray(response.usedCards)) {
        console.log(`[TournamentEntry] loadUsedCardsInGameweek - Loaded ${response.usedCards.length} used cards for gameweek ID ${gameweekId}:`, response.usedCards);
        setUsedCardsInOtherTournaments(response.usedCards);
        return response.usedCards;
      }
      
      console.warn('[TournamentEntry] loadUsedCardsInGameweek - No used cards returned or invalid response:', response);
      return [];
    } catch (error) {
      console.error('[TournamentEntry] Error loading used cards in gameweek:', error);
      return [];
    }
  };

  const loadUserCards = useCallback(async (positionFilter = null) => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const filtersToUse = { ...filters };
      
      // CRITICAL: Filter by tournament rarity restriction
      // If tournament is basic, only show basic cards, etc.
      if (tournament?.rarity_restriction && tournament.rarity_restriction !== 'all') {
        filtersToUse.rarity = tournament.rarity_restriction;
      }
      
      if (positionFilter) {
        // Filtra per posizione in base al ruolo selezionato
        if (positionFilter.role === 'GK') {
          filtersToUse.position = 'GK';
        } else if (positionFilter.role === 'DEF' || positionFilter.role === 'RES1') {
          filtersToUse.position = 'DEF';
        } else if (positionFilter.role === 'MID' || positionFilter.role === 'RES2') {
          filtersToUse.position = 'MID';
        } else if (positionFilter.role === 'ATT' || positionFilter.role === 'RES3') {
          filtersToUse.position = 'ATT';
        } else if (positionFilter.role === 'LEGEND') {
          filtersToUse.rarity = 'legend';
        } else if (positionFilter.role === 'DIVINITY') {
          filtersToUse.rarity = 'divinity';
        }
      }
      const response = await cardsApi.getUserCards(user.id, filtersToUse);
      if (response?.success) {
        const loadedCards = response.cards || [];
        
        if (loadedCards.length > 0 && tournament) {
          const enrichedCards = await fetchNextMatchForCards(loadedCards, tournament);
          setUserCards(filterWorldCupConvocatedCards(enrichedCards, tournament));
        } else {
          setUserCards(loadedCards);
        }
      }
    } catch (error) {
      console.error('Error loading cards:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id, filters, tournament?.rarity_restriction, tournament]);

  // Carica le card quando si apre il modal o quando cambiano i filtri
  useEffect(() => {
    if (modalOpen && selectedPosition) {
      loadUserCards(selectedPosition);
    }
  }, [modalOpen, selectedPosition, loadUserCards, filters]);

  const handlePositionClick = (role, index = null) => {
    setSelectedPosition({ role, index });
    const counts = getFormationCounts();
    if (role === 'DEF') {
      setPendingLineSelection({ role: 'DEF', selected: [...(team.defenders || [])], max: counts.maxDef });
    } else if (role === 'MID') {
      setPendingLineSelection({ role: 'MID', selected: [...(team.midfielders || [])], max: counts.maxMid });
    } else if (role === 'ATT') {
      setPendingLineSelection({ role: 'ATT', selected: [...(team.attackers || [])], max: counts.maxAtt });
    } else {
      setPendingLineSelection(null);
    }
    setModalOpen(true);
  };

  const handleConfirmLineSelection = () => {
    if (!pendingLineSelection) return;
    const { role, selected } = pendingLineSelection;
    if (role === 'DEF') setTeam(prev => ({ ...prev, defenders: [...selected] }));
    else if (role === 'MID') setTeam(prev => ({ ...prev, midfielders: [...selected] }));
    else if (role === 'ATT') setTeam(prev => ({ ...prev, attackers: [...selected] }));
    setPendingLineSelection(null);
    setModalOpen(false);
    setSelectedPosition(null);
  };

  const handleCardSelectFromModal = (card) => {
    if (!selectedPosition) return;
    
    // CRITICAL CHECK: prevent selecting cards already used in other tournaments
    if (usedCardsInOtherTournaments.includes(card.id)) {
      console.warn(`[TournamentEntry] Card ${card.id} is already used in another tournament of this gameweek - selection blocked`);
      return;
    }

    const isOutOfDios10 =
      card?.player?.is_out_of_dios10 === true ||
      card?.player?.is_out_of_dios10 === 1 ||
      card?.player?.is_out_of_dios10 === '1' ||
      card?.player?.isOutOfDios10 === true ||
      card?.player?.isOutOfDios10 === 1 ||
      card?.player?.isOutOfDios10 === '1';
    if (isOutOfDios10) {
      console.warn(`[TournamentEntry] Card ${card.id} is OUT OF DIOS - selection blocked`);
      return;
    }
    const isLegendSlot = selectedPosition.role === 'LEGEND' || selectedPosition.role === 'DIVINITY';
    if (!isLegendSlot && isMissingUpcomingMatchFirst(card)) {
      return;
    }
    
    const { role } = selectedPosition;

    // Multi-select per DEF/MID/ATT: non chiudere il modal, aggiungi/rimuovi dalla lista
    if (pendingLineSelection) {
      const { selected, max } = pendingLineSelection;
      const idx = selected.findIndex(c => c.id === card.id);
      if (idx >= 0) {
        const next = selected.filter((_, i) => i !== idx);
        setPendingLineSelection({ ...pendingLineSelection, selected: next });
      } else if (selected.length < max) {
        setPendingLineSelection({ ...pendingLineSelection, selected: [...selected, card] });
      }
      return;
    }

    handleCardSelect(card, role);
    setModalOpen(false);
    setSelectedPosition(null);
  };

  const handleCardSelect = (card, position) => {
    // CRITICAL CHECK: prevent selecting cards already used in other tournaments
    // Cards are already disabled in the UI, so just silently return if somehow clicked
    if (usedCardsInOtherTournaments.includes(card.id)) {
      console.warn(`[TournamentEntry] Card ${card.id} is already used in another tournament of this gameweek - selection blocked`);
      return;
    }
    const isOutOfDios10 =
      card?.player?.is_out_of_dios10 === true ||
      card?.player?.is_out_of_dios10 === 1 ||
      card?.player?.is_out_of_dios10 === '1' ||
      card?.player?.isOutOfDios10 === true ||
      card?.player?.isOutOfDios10 === 1 ||
      card?.player?.isOutOfDios10 === '1';
    if (isOutOfDios10) {
      console.warn(`[TournamentEntry] Card ${card.id} is OUT OF DIOS - selection blocked`);
      return;
    }

    const isAlreadyInDefendersEarly = team.defenders.find(c => c.id === card.id);
    const isAlreadyInMidfieldersEarly = team.midfielders.find(c => c.id === card.id);
    const isAlreadyInAttackersEarly = team.attackers.find(c => c.id === card.id);
    const isAlreadyInReservesEarly =
      team.reserves.RES1?.id === card.id ||
      team.reserves.RES2?.id === card.id ||
      team.reserves.RES3?.id === card.id;
    const isAlreadyInLegendEarly = team.legend?.id === card.id;
    const isAlreadyPlacedInTeam = !!(
      team.goalkeeper?.id === card.id ||
      isAlreadyInDefendersEarly ||
      isAlreadyInMidfieldersEarly ||
      isAlreadyInAttackersEarly ||
      isAlreadyInReservesEarly ||
      isAlreadyInLegendEarly
    );

    if (
      position !== 'LEGEND' &&
      position !== 'DIVINITY' &&
      !isAlreadyPlacedInTeam &&
      isMissingUpcomingMatchFirst(card)
    ) {
      return;
    }
    
    const cardPosition = card.player?.position?.toUpperCase() || '';
    const isAlreadyInDefenders = isAlreadyInDefendersEarly;
    const isAlreadyInMidfielders = isAlreadyInMidfieldersEarly;
    const isAlreadyInAttackers = isAlreadyInAttackersEarly;
    const isAlreadyInReserves = isAlreadyInReservesEarly;
    const isAlreadyInLegend = isAlreadyInLegendEarly;
    const isAlreadyInTeam = isAlreadyPlacedInTeam;
    const cardKind = String(card.card_kind || card.cardKind || '').toLowerCase();
    const rarityName = String(card.rarity?.name || '').toLowerCase();
    const hasLegendFlag = !!card.player?.is_legend;
    const hasDivinityFlag = !!card.player?.is_divinity;
    const isLegendCard = cardKind === 'legend' || rarityName === 'legend' || hasLegendFlag;
    const isDivinityCard = cardKind === 'divinity' || rarityName === 'divinity' || hasDivinityFlag;

    if (position === 'LEGEND' || position === 'DIVINITY') {
      if (position === 'LEGEND' && !isLegendCard) {
        alert('Puoi selezionare solo carte Legend in questo slot.');
        return;
      }
      if (position === 'DIVINITY' && !isDivinityCard) {
        alert('Puoi selezionare solo carte Divinity in questo slot.');
        return;
      }
      if (isAlreadyInTeam && !isAlreadyInLegend) {
        alert('This player is already selected in another position. Please remove them first.');
        return;
      }
      setTeam({ ...team, legend: isAlreadyInLegend ? null : card });
      return;
    }
    
    // Calcola il totale attuale di giocatori in campo (escluso portiere e riserve)
    const totalFieldPlayers = team.defenders.length + team.midfielders.length + team.attackers.length;
    const maxFieldPlayers = 10; // 11 totali - 1 portiere = 10 giocatori di campo
    const isFieldComplete = team.goalkeeper && totalFieldPlayers === 10;
    
    // Se il campo è completo, gestisci solo le riserve
    if (isFieldComplete && !isAlreadyInReserves && !isAlreadyInDefenders && !isAlreadyInMidfielders && !isAlreadyInAttackers) {
      if (cardPosition.startsWith('DEF') && !team.reserves.RES1) {
        setTeam({ ...team, reserves: { ...team.reserves, RES1: card } });
        return;
      } else if (cardPosition.startsWith('MID') && !team.reserves.RES2) {
        setTeam({ ...team, reserves: { ...team.reserves, RES2: card } });
        return;
      } else if (cardPosition.startsWith('ATT') && !team.reserves.RES3) {
        setTeam({ ...team, reserves: { ...team.reserves, RES3: card } });
        return;
      }
    }
    
    // Se non è completo il campo, gestisci i giocatori in campo
    if (position === 'GK') {
      setTeam({ ...team, goalkeeper: team.goalkeeper?.id === card.id ? null : card });
    } else if (position === 'DEF') {
      // Se è già nelle riserve, rimuovilo
      if (team.reserves.RES1?.id === card.id) {
        setTeam({ ...team, reserves: { ...team.reserves, RES1: null } });
      }
      // Parse formation per ottenere il numero massimo di difensori
      const parts = formation.split('-').map(Number);
      const maxDef = parts.length >= 1 ? parts[0] : 4;
      
      // Gestisci difensori principali - deve corrispondere alla formazione
      const defenders = team.defenders.filter(c => c.id !== card.id);
      if (!isAlreadyInDefenders) {
        // Verifica che non superiamo il numero di difensori della formazione
        if (defenders.length < maxDef) {
          setTeam({ ...team, defenders: [...defenders, card] });
        } else {
          alert(`Formation ${formation} requires exactly ${maxDef} defenders. Current: ${team.defenders.length}`);
        }
      } else {
        setTeam({ ...team, defenders });
      }
    } else if (position === 'MID') {
      // Se è già nelle riserve, rimuovilo
      if (team.reserves.RES2?.id === card.id) {
        setTeam({ ...team, reserves: { ...team.reserves, RES2: null } });
      }
      // Parse formation per ottenere il numero massimo di centrocampisti
      const parts = formation.split('-').map(Number);
      const maxMid = parts.length === 3 ? parts[1] : parts.length === 4 ? parts[1] + parts[2] : parts.length === 5 ? parts[1] + parts[2] : 3;
      
      // Gestisci centrocampisti principali - deve corrispondere alla formazione
      const midfielders = team.midfielders.filter(c => c.id !== card.id);
      if (!isAlreadyInMidfielders) {
        if (midfielders.length < maxMid) {
          setTeam({ ...team, midfielders: [...midfielders, card] });
        } else {
          alert(`Formation ${formation} requires exactly ${maxMid} midfielders. Current: ${team.midfielders.length}`);
        }
      } else {
        setTeam({ ...team, midfielders });
      }
    } else if (position === 'ATT') {
      // Se è già nelle riserve, rimuovilo
      if (team.reserves.RES3?.id === card.id) {
        setTeam({ ...team, reserves: { ...team.reserves, RES3: null } });
      }
      // Parse formation per ottenere il numero massimo di attaccanti
      const parts = formation.split('-').map(Number);
      const maxAtt = parts.length === 3 ? parts[2] : parts.length === 4 ? parts[3] : parts.length === 5 ? parts[3] + parts[4] : 3;
      
      // Gestisci attaccanti principali - deve corrispondere alla formazione
      const attackers = team.attackers.filter(c => c.id !== card.id);
      if (!isAlreadyInAttackers) {
        if (attackers.length < maxAtt) {
          setTeam({ ...team, attackers: [...attackers, card] });
        } else {
          alert(`Formation ${formation} requires exactly ${maxAtt} attackers. Current: ${team.attackers.length}`);
        }
      } else {
        setTeam({ ...team, attackers });
      }
    }
    
    // Gestione riserve quando si clicca su una card già nelle riserve
    if (isAlreadyInReserves) {
      if (team.reserves.RES1?.id === card.id) {
        setTeam({ ...team, reserves: { ...team.reserves, RES1: null } });
      } else if (team.reserves.RES2?.id === card.id) {
        setTeam({ ...team, reserves: { ...team.reserves, RES2: null } });
      } else if (team.reserves.RES3?.id === card.id) {
        setTeam({ ...team, reserves: { ...team.reserves, RES3: null } });
      }
    }
  };

  const handleSubmit = async () => {
    // Parse formation per verificare che corrisponda
    const parts = formation.split('-').map(Number);
    let expectedDef = 0, expectedMid = 0, expectedAtt = 0;
    
    if (parts.length === 3) {
      expectedDef = parts[0];
      expectedMid = parts[1];
      expectedAtt = parts[2];
    } else if (parts.length === 4) {
      expectedDef = parts[0];
      expectedMid = parts[1] + parts[2];
      expectedAtt = parts[3];
    } else if (parts.length === 5) {
      expectedDef = parts[0];
      expectedMid = parts[1] + parts[2];
      expectedAtt = parts[3] + parts[4];
    }
    
    // Verifica requisiti minimi: 1 GK, minimo 3 DEF, minimo 3 MID, minimo 1 ATT
    if (!team.goalkeeper) {
      alert('Please select 1 goalkeeper');
      return;
    }
    
    if (team.defenders.length < 3) {
      alert('Please select at least 3 defenders');
      return;
    }
    
    if (team.midfielders.length < 3) {
      alert('Please select at least 3 midfielders');
      return;
    }
    
    if (team.attackers.length < 1) {
      alert('Please select at least 1 attacker');
      return;
    }
    
    // Verifica che corrisponda alla formazione selezionata
    if (team.defenders.length !== expectedDef || team.midfielders.length !== expectedMid || team.attackers.length !== expectedAtt) {
      alert(`Team composition doesn't match formation ${formation}. Expected: ${expectedDef} DEF, ${expectedMid} MID, ${expectedAtt} ATT. Current: ${team.defenders.length} DEF, ${team.midfielders.length} MID, ${team.attackers.length} ATT.`);
      return;
    }
    
    // Verifica che ci siano esattamente 11 giocatori in campo (1 GK + 10 di campo)
    const totalFieldPlayers = team.defenders.length + team.midfielders.length + team.attackers.length;
    if (totalFieldPlayers !== 10) {
      alert(`You must have exactly 11 players on the field (1 GK + ${totalFieldPlayers} field players). Required: 1 GK + 10 field players.`);
      return;
    }
    
    // Verifica riserve (obbligatorie)
    if (!team.reserves.RES1 || !team.reserves.RES2 || !team.reserves.RES3) {
      alert('Please select all 3 reserves: RES1 (defender), RES2 (midfielder), RES3 (attacker)');
      return;
    }
    
    // Verifica che RES1 sia un difensore
    if (!team.reserves.RES1.player?.position?.toUpperCase().startsWith('DEF')) {
      alert('RES1 must be a defender');
      return;
    }
    
    // Verifica che RES2 sia un centrocampista
    if (!team.reserves.RES2.player?.position?.toUpperCase().startsWith('MID')) {
      alert('RES2 must be a midfielder');
      return;
    }
    
    // Verifica che RES3 sia un attaccante
    if (!team.reserves.RES3.player?.position?.toUpperCase().startsWith('ATT')) {
      alert('RES3 must be an attacker');
      return;
    }

    setSubmitting(true);
    try {
      // Prepare team with match_id for each card
      const teamWithMatchIds = {
        ...team,
        goalkeeper: team.goalkeeper ? { ...team.goalkeeper, match_id: team.goalkeeper.match_id || null } : null,
        defenders: team.defenders.map(def => ({ ...def, match_id: def.match_id || null })),
        midfielders: team.midfielders.map(mid => ({ ...mid, match_id: mid.match_id || null })),
        attackers: team.attackers.map(att => ({ ...att, match_id: att.match_id || null })),
        reserves: {
          RES1: team.reserves.RES1 ? { ...team.reserves.RES1, match_id: team.reserves.RES1.match_id || null } : null,
          RES2: team.reserves.RES2 ? { ...team.reserves.RES2, match_id: team.reserves.RES2.match_id || null } : null,
          RES3: team.reserves.RES3 ? { ...team.reserves.RES3, match_id: team.reserves.RES3.match_id || null } : null
        }
      };
      
      const response = await tournamentsApi.enter(parseInt(id), {
        userId: user.id,
        buyInAmount: tournament.buy_in_amount || 0,
        team: teamWithMatchIds,
        formation: formation
      });
      
      if (response?.success) {
        navigate('/profile');
      }
    } catch (error) {
      console.error('Error entering tournament:', error);
      alert('Failed to enter tournament: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!tournament) {
    return (
      <div className="tournament-entry-container">
        <div className="tournament-entry-background" style={{
          backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%), url('/images/homebg.png')`
        }}></div>
        <div className="min-h-screen flex items-center justify-center relative z-10">
          <div className="football-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="tournament-entry-container">
      {/* Tournament Entry Background */}
      <div 
        className="tournament-entry-background"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%), url('/images/homebg.png')`
        }}
      ></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <h1 className="text-4xl font-bold mb-8">{tournament.name}</h1>

        {/* Team Formation */}
        <div className="football-card glass-football p-6 mb-6">
          <h2 className="text-2xl font-bold mb-6">Select Your Team</h2>
          
          {/* Formation Selector */}
          <div className="mb-6">
            <label className="block font-semibold mb-2">Select Formation</label>
            <select
              value={formation}
              onChange={(e) => setFormation(e.target.value)}
              className="w-full md:w-auto min-w-[200px]"
            >
              {validFormations.map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
            <p className="text-sm mt-2">
              Selected: {formation} - Click on player cards below to assign them to positions
            </p>
          </div>
          
          {/* Football Field with Players */}
          <div className="formation-field-panel bg-white/80 backdrop-blur-xl p-6 mb-6 border border-[rgba(65,105,225,0.2)]">
            <FootballField 
              formation={formation}
              team={team}
              onPositionClick={handlePositionClick}
              onPlayerRemove={handleCardSelect}
              allowLegends={toBoolFlag(tournament?.allow_legends ?? tournament?.allowLegends ?? tournament?.allow_legend ?? tournament?.allowLegend)}
              allowDivinities={toBoolFlag(tournament?.allow_divinities ?? tournament?.allowDivinities ?? tournament?.allow_divinity ?? tournament?.allowDivinity)}
              user={user}
              usedCardsInActiveGameweeks={usedCardsInOtherTournaments}
            />
          </div>
          
          {/* Old Formation Display - Hidden */}
          <div className="bg-white/80 backdrop-blur-xl rounded-lg p-6 mb-6 hidden border border-[rgba(65,105,225,0.2)]">
            <div className="text-center mb-4">
              <div className="text-yellow-400 font-bold mb-2">Goalkeeper (1)</div>
              {team.goalkeeper ? (
                <div className="bg-gray-700 p-4 rounded-lg">
                  {team.goalkeeper.player?.display_name || team.goalkeeper.player?.last_name || team.goalkeeper.player?.full_name?.split(' ').slice(-1)[0] || 'Card #' + team.goalkeeper.id}
                </div>
              ) : (
                <div className="bg-gray-700/50 p-4 rounded-lg text-gray-500">Select GK</div>
              )}
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <div className="text-yellow-400 font-bold mb-2 text-center text-sm">
                  Defenders (min 3, current: {team.defenders.length})
                </div>
                {Array.from({ length: Math.max(3, team.defenders.length + 1) }, (_, i) => {
                  const maxDef = 10 - team.midfielders.length - team.attackers.length;
                  const isRequired = i < 3;
                  const canAdd = i < maxDef;
                  return (
                    <div 
                      key={i} 
                      className={`bg-gray-700 p-2 rounded mb-2 text-center text-sm ${
                        !canAdd ? 'opacity-50' : ''
                      }`}
                    >
                      {team.defenders[i] ? (team.defenders[i].player?.display_name || team.defenders[i].player?.last_name || team.defenders[i].player?.full_name?.split(' ').slice(-1)[0] || 'Player') : (isRequired ? 'DEF (required)' : canAdd ? 'DEF (optional)' : 'DEF (max)')}
                    </div>
                  );
                })}
              </div>
              <div>
                <div className="text-yellow-400 font-bold mb-2 text-center text-sm">
                  Midfielders (min 3, current: {team.midfielders.length})
                </div>
                {Array.from({ length: Math.max(3, team.midfielders.length + 1) }, (_, i) => {
                  const maxMid = 10 - team.defenders.length - team.attackers.length;
                  const isRequired = i < 3;
                  const canAdd = i < maxMid;
                  return (
                    <div 
                      key={i} 
                      className={`bg-gray-700 p-2 rounded mb-2 text-center text-sm ${
                        !canAdd ? 'opacity-50' : ''
                      }`}
                    >
                      {team.midfielders[i] ? (team.midfielders[i].player?.display_name || team.midfielders[i].player?.last_name || team.midfielders[i].player?.full_name?.split(' ').slice(-1)[0] || 'Player') : (isRequired ? 'MID (required)' : canAdd ? 'MID (optional)' : 'MID (max)')}
                    </div>
                  );
                })}
              </div>
              <div>
                <div className="text-yellow-400 font-bold mb-2 text-center text-sm">
                  Attackers (min 1, current: {team.attackers.length})
                </div>
                {Array.from({ length: Math.max(1, team.attackers.length + 1) }, (_, i) => {
                  const maxAtt = 10 - team.defenders.length - team.midfielders.length;
                  const isRequired = i < 1;
                  const canAdd = i < maxAtt;
                  return (
                    <div 
                      key={i} 
                      className={`bg-gray-700 p-2 rounded mb-2 text-center text-sm ${
                        !canAdd ? 'opacity-50' : ''
                      }`}
                    >
                      {team.attackers[i] ? (team.attackers[i].player?.display_name || team.attackers[i].player?.last_name || team.attackers[i].player?.full_name?.split(' ').slice(-1)[0] || 'Player') : (isRequired ? 'ATT (required)' : canAdd ? 'ATT (optional)' : 'ATT (max)')}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="text-center text-sm text-[#64748b] mb-4">
              Field players: {team.defenders.length + team.midfielders.length + team.attackers.length} / 10 (required: exactly 10)
            </div>

            {/* Reserves */}
            <div className="mt-4 pt-4 border-t border-[rgba(65,105,225,0.2)]">
              <div className="text-yellow-400 font-bold mb-3 text-center">
                Reserves (Select after completing 11 field players)
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-yellow-400 font-bold mb-2 text-center text-sm">RES1 (Defender)</div>
                  <div 
                    className={`bg-gray-700 p-2 rounded text-center text-xs cursor-pointer hover:bg-gray-600 transition ${
                      team.reserves.RES1 ? 'ring-2 ring-green-400' : 'border-2 border-dashed border-gray-600'
                    }`}
                    onClick={() => {
                      if (team.reserves.RES1) {
                        setTeam({ ...team, reserves: { ...team.reserves, RES1: null } });
                      }
                    }}
                  >
                    {team.reserves.RES1 ? (
                      <div>
                        <div className="font-semibold">{team.reserves.RES1.player?.last_name || team.reserves.RES1.player?.full_name?.split(' ').slice(-1)[0] || 'Player'}</div>
                        <div className="text-xs text-gray-400 mt-1">Click to remove</div>
                      </div>
                    ) : (
                      <div className="text-gray-500">Click DEF card below</div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-yellow-400 font-bold mb-2 text-center text-sm">RES2 (Midfielder)</div>
                  <div 
                    className={`bg-gray-700 p-2 rounded text-center text-xs cursor-pointer hover:bg-gray-600 transition ${
                      team.reserves.RES2 ? 'ring-2 ring-green-400' : 'border-2 border-dashed border-gray-600'
                    }`}
                    onClick={() => {
                      if (team.reserves.RES2) {
                        setTeam({ ...team, reserves: { ...team.reserves, RES2: null } });
                      }
                    }}
                  >
                    {team.reserves.RES2 ? (
                      <div>
                        <div className="font-semibold">{team.reserves.RES2.player?.last_name || team.reserves.RES2.player?.full_name?.split(' ').slice(-1)[0] || 'Player'}</div>
                        <div className="text-xs text-gray-400 mt-1">Click to remove</div>
                      </div>
                    ) : (
                      <div className="text-gray-500">Click MID card below</div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-yellow-400 font-bold mb-2 text-center text-sm">RES3 (Attacker)</div>
                  <div 
                    className={`bg-gray-700 p-2 rounded text-center text-xs cursor-pointer hover:bg-gray-600 transition ${
                      team.reserves.RES3 ? 'ring-2 ring-green-400' : 'border-2 border-dashed border-gray-600'
                    }`}
                    onClick={() => {
                      if (team.reserves.RES3) {
                        setTeam({ ...team, reserves: { ...team.reserves, RES3: null } });
                      }
                    }}
                  >
                    {team.reserves.RES3 ? (
                      <div>
                        <div className="font-semibold">{team.reserves.RES3.player?.last_name || team.reserves.RES3.player?.full_name?.split(' ').slice(-1)[0] || 'Player'}</div>
                        <div className="text-xs text-gray-400 mt-1">Click to remove</div>
                      </div>
                    ) : (
                      <div className="text-gray-500">Click ATT card below</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {tournament.allow_legends && (
              <div className="text-center mt-4">
                <div className="text-yellow-400 font-bold mb-2">Legend/Divinity (Optional)</div>
                <div className="bg-gray-700 p-4 rounded-lg">
                  {team.legend ? (team.legend.player?.last_name || team.legend.player?.full_name?.split(' ').slice(-1)[0] || 'Player') : 'Select Legend'}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="btn-football w-full text-lg py-3 flex items-center justify-center space-x-2"
          >
            <Play className="w-5 h-5" />
            <span>{submitting ? 'Entering...' : 'Enter Tournament'}</span>
          </button>
        </div>

        {/* Player Selection Modal - Portal to body to avoid parent scroll/transform; dark overlay like app; below navbar */}
        {modalOpen && selectedPosition && createPortal(
          <div className="tournament-entry-player-modal formation-player-modal fixed inset-0 z-[99999] bg-black/85 backdrop-blur-md flex items-start justify-center pt-[max(120px,env(safe-area-inset-top,0px)+80px)] md:pt-[56px] p-4 overflow-y-auto">
            <div className="football-card glass-football p-6 max-w-4xl w-full flex flex-col min-h-0 overflow-hidden flex-shrink-0 max-h-[calc(100vh-136px)] md:max-h-[calc(100vh-72px)]">
              <div className="flex-shrink-0 flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">
                  Select {selectedPosition.role === 'GK' ? 'Goalkeeper' : 
                          selectedPosition.role === 'DEF' ? 'Defender' : 
                          selectedPosition.role === 'MID' ? 'Midfielder' : 
                          selectedPosition.role === 'ATT' ? 'Attacker' :
                          selectedPosition.role === 'RES1' ? 'Reserve 1 (Defender)' :
                          selectedPosition.role === 'RES2' ? 'Reserve 2 (Midfielder)' :
                          selectedPosition.role === 'RES3' ? 'Reserve 3 (Attacker)' :
                          selectedPosition.role === 'LEGEND' ? 'Legend' :
                          'Divinity'}
                </h2>
                <div className="flex items-center gap-3">
                  {pendingLineSelection && (
                    <>
                      <span className="text-sm font-medium text-gray-700">
                        Selezionati: {pendingLineSelection.selected.length} / {pendingLineSelection.max}
                      </span>
                      <button
                        type="button"
                        onClick={handleConfirmLineSelection}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700"
                      >
                        Conferma
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setModalOpen(false);
                      setSelectedPosition(null);
                      setPendingLineSelection(null);
                    }}
                    className="modal-close-btn"
                    aria-label="Chiudi"
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Filters in Modal */}
              <div className="flex-shrink-0 grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="md:col-span-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search player..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                      className="pl-10 w-full"
                    />
                  </div>
                </div>
                <select
                  value={tournament?.rarity_restriction && tournament.rarity_restriction !== 'all' 
                    ? tournament.rarity_restriction 
                    : filters.rarity}
                  onChange={(e) => {
                    // Only allow changing if tournament allows all rarities
                    if (tournament?.rarity_restriction && tournament.rarity_restriction !== 'all') {
                      return; // Don't allow changing if tournament has specific rarity restriction
                    }
                    setFilters({ ...filters, rarity: e.target.value });
                  }}
                  disabled={tournament?.rarity_restriction && tournament.rarity_restriction !== 'all'}
                  className=""
                  title={tournament?.rarity_restriction && tournament.rarity_restriction !== 'all' 
                    ? `This tournament only allows ${tournament.rarity_restriction} cards` 
                    : 'Filter by rarity'}
                >
                  <option value="">All Rarities</option>
                  <option value="basic">Basic</option>
                  <option value="plus">Plus</option>
                  <option value="rare">Rare</option>
                  <option value="epic">Epic</option>
                  <option value="unique">Unique</option>
                  <option value="legend">Legend</option>
                  <option value="divinity">Divinity</option>
                </select>
                <input
                  type="text"
                  placeholder="Nationality/Team"
                  value={filters.nationality}
                  onChange={(e) => setFilters({ ...filters, nationality: e.target.value })}
                  className=""
                />
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto">
                {loading ? (
                  <div className="text-center py-12">
                    <div className="football-spinner mx-auto"></div>
                    <p className="mt-4">Loading cards...</p>
                  </div>
                ) : (() => {
                  const isLegendSlot =
                    selectedPosition?.role === 'LEGEND' || selectedPosition?.role === 'DIVINITY';
                  const visibleCards = filterFormationCardsWithoutNa(userCards, {
                    isLegendSlot,
                    teamCardIds: collectFormationTeamCardIds(team, pendingLineSelection),
                  });
                  if (visibleCards.length === 0) {
                    return (
                      <div className="text-center py-12">
                        {isWorldCupTournament(tournament) || userCards.length > 0 ? (
                          <p className="text-gray-300">
                            Nessuna carta con partita disponibile per questo ruolo in questa gameweek.
                          </p>
                        ) : (
                          <>
                            No {selectedPosition.role === 'GK' ? 'goalkeepers' :
                                selectedPosition.role === 'DEF' || selectedPosition.role === 'RES1' ? 'defenders' :
                                selectedPosition.role === 'MID' || selectedPosition.role === 'RES2' ? 'midfielders' :
                                selectedPosition.role === 'ATT' || selectedPosition.role === 'RES3' ? 'attackers' :
                                selectedPosition.role === 'LEGEND' ? 'legend cards' :
                                'divinity cards'} available. Purchase packs to get cards!
                          </>
                        )}
                      </div>
                    );
                  }
                  return (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {visibleCards.map((card) => {
                    const isInPendingLine = pendingLineSelection?.selected.some(c => c.id === card.id);
                    const isInCurrentTeam = pendingLineSelection
                      ? isInPendingLine
                      : (team.goalkeeper?.id === card.id ||
                        team.defenders.find(c => c.id === card.id) ||
                        team.midfielders.find(c => c.id === card.id) ||
                        team.attackers.find(c => c.id === card.id) ||
                        team.legend?.id === card.id ||
                        team.reserves.RES1?.id === card.id ||
                        team.reserves.RES2?.id === card.id ||
                        team.reserves.RES3?.id === card.id);
                    const atLimitNoSelected = pendingLineSelection && !isInPendingLine && pendingLineSelection.selected.length >= pendingLineSelection.max;
                    
                    // Verifica se la card è già usata in altri tornei del gameweek
                    const isUsedInOtherTournament = usedCardsInOtherTournaments.includes(card.id);
                    
                    // Check if this card's next match is within range
                    const showsNotPlay = card.playsInRange === false;
                    const isOutOfDios10 =
                      card.player?.is_out_of_dios10 === true ||
                      card.player?.is_out_of_dios10 === 1 ||
                      card.player?.is_out_of_dios10 === '1' ||
                      card.player?.isOutOfDios10 === true ||
                      card.player?.isOutOfDios10 === 1 ||
                      card.player?.isOutOfDios10 === '1';
                    const isLegendSlot = selectedPosition?.role === 'LEGEND' || selectedPosition?.role === 'DIVINITY';
                    const formatUtc = (m) =>
                      m?.utcDate ? new Date(m.utcDate).toLocaleString() : null;
                    const match1Label = formatUpcomingMatchFirstLabel(card);
                    const match2Label = formatUtc(card.upcoming_match_second);
                    const showsNoMatch = !isLegendSlot && isMissingUpcomingMatchFirst(card);
                    
                    // Determina la posizione nella formazione corrente
                    let currentPosition = null;
                    if (team.goalkeeper?.id === card.id) currentPosition = 'GK';
                    else if (team.defenders.find(c => c.id === card.id)) currentPosition = 'DEF';
                    else if (team.midfielders.find(c => c.id === card.id)) currentPosition = 'MID';
                    else if (team.attackers.find(c => c.id === card.id)) currentPosition = 'ATT';
                    else if (team.legend?.id === card.id) {
                      const k = String(card.card_kind || card.cardKind || '').toLowerCase();
                      const r = String(card.rarity?.name || '').toLowerCase();
                      const isDivinity = k === 'divinity' || r === 'divinity' || !!card.player?.is_divinity;
                      currentPosition = isDivinity ? 'DIVINITY' : 'LEGEND';
                    }
                    else if (team.reserves.RES1?.id === card.id) currentPosition = 'RES1';
                    else if (team.reserves.RES2?.id === card.id) currentPosition = 'RES2';
                    else if (team.reserves.RES3?.id === card.id) currentPosition = 'RES3';
                    
                    // In multi-select: disable if used elsewhere, at limit (and not already selected), or doesn't play.
                    // Single-select: also disable if card is in team but in a different role.
                    const isDisabled = isUsedInOtherTournament || showsNotPlay || showsNoMatch || isOutOfDios10 ||
                      (pendingLineSelection ? atLimitNoSelected : (isInCurrentTeam && currentPosition && currentPosition !== selectedPosition.role));
                    
                    return (
                      <button
                        key={card.id}
                        onClick={() => {
                          if (!isDisabled) {
                            handleCardSelectFromModal(card);
                          }
                        }}
                        disabled={isDisabled}
                        className={`football-card player-pick-card p-4 text-center transition-all relative ${
                          isDisabled 
                            ? 'opacity-50 cursor-not-allowed' 
                            : 'hover:scale-105 cursor-pointer'
                        } ${
                          isInCurrentTeam 
                            ? 'ring-2 ring-yellow-400 bg-yellow-400/20' 
                            : ''
                        }`}
                        title={
                          isOutOfDios10
                            ? 'This player is OUT OF DIOS and cannot be selected'
                            : showsNoMatch
                            ? 'Nessuna partita disponibile per questo giocatore'
                            : showsNotPlay
                            ? 'This player\'s team does not play during this tournament'
                            : isDisabled 
                              ? 'This player is already used in another tournament of this gameweek' 
                              : isInCurrentTeam 
                                ? `Already selected as ${currentPosition}` 
                                : 'Click to select'
                        }
                      >
                        {isInCurrentTeam && (
                          <div className="absolute top-2 right-2 bg-yellow-400 text-black text-xs font-bold px-2 py-1 rounded z-10">
                            {currentPosition}
                          </div>
                        )}
                        {isUsedInOtherTournament && (
                          <div className="absolute top-2 left-2 bg-red-100 border border-red-300 text-red-800 text-xs font-bold px-2 py-1 rounded z-10">
                            Used
                          </div>
                        )}
                        {showsNotPlay && (
                          <div className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded z-10 text-shadow-sm">
                            NOT PLAY
                          </div>
                        )}
                        {showsNoMatch && (
                          <div className="absolute top-2 left-2 bg-gray-700 text-white text-xs font-bold px-2 py-1 rounded z-10 text-shadow-sm">
                            N/A
                          </div>
                        )}
                        {isOutOfDios10 && (
                          <div className="absolute top-10 left-2 bg-red-900/95 border border-red-600 text-red-100 text-xs font-bold px-2 py-1 rounded z-10 text-shadow-sm">
                            OUT OF DIOS
                          </div>
                        )}
                        <div className={`w-full h-32 bg-gradient-to-br ${
                          isDisabled 
                            ? 'from-gray-300 to-gray-400' 
                            : 'from-[#4169E1] to-[#1E40AF]'
                        } rounded-lg flex flex-col items-center justify-center mb-2`}>
                          <div className="text-white text-sm font-bold text-center whitespace-normal leading-tight px-1">
                            {(() => {
                              const cardKind = String(card.card_kind || card.cardKind || '').toLowerCase();
                              const rarityName = String(card.rarity?.name || '').toLowerCase();
                              const isSpecialCard =
                                cardKind === 'legend' ||
                                cardKind === 'divinity' ||
                                rarityName === 'legend' ||
                                rarityName === 'divinity' ||
                                !!card.player?.is_legend ||
                                !!card.player?.is_divinity;
                              if (isSpecialCard) {
                                return (
                                  card.custom_name ||
                                  card.customName ||
                                  card.player?.display_name ||
                                  card.player?.full_name ||
                                  card.player?.last_name ||
                                  'Player'
                                );
                              }
                              return card.player?.display_name || card.player?.last_name || card.player?.full_name?.split(' ').slice(-1)[0] || 'Player';
                            })()}
                          </div>
                          {card.player?.team && (
                            <div className="text-white/90 text-xs mt-1 text-center">
                              {card.player.team}
                            </div>
                          )}
                        </div>
                        <div className="player-pick-card-meta mt-1.5 text-sm sm:text-base font-bold text-center leading-snug text-white">
                          {card.rarity?.display_name || 'Basic'} · {card.player?.position || 'N/A'}
                          {(card.xp_percentage != null || card.xpPercentage != null) && (
                            <>
                              {' '}
                              · XP&nbsp;{Number(card.xp_percentage ?? card.xpPercentage ?? 0).toFixed(1)}%
                            </>
                          )}
                        </div>
                        <div className="mt-1 space-y-0.5 text-xs text-center leading-snug text-gray-300">
                          <div>{match1Label ? `Match 1: ${match1Label}` : 'Match 1: N/A'}</div>
                          <div>{match2Label ? `Match 2: ${match2Label}` : 'Match 2: —'}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                  );
                })()}
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

const CardSelectItem = ({ card, onSelect, team }) => {
  const position = card.player?.position || 'MID';
  const positionUpper = position?.toUpperCase() || '';
  
  // Determina dove è selezionata la card
  const isInGoalkeeper = team.goalkeeper?.id === card.id;
  const isInDefenders = team.defenders.find(c => c.id === card.id);
  const isInMidfielders = team.midfielders.find(c => c.id === card.id);
  const isInAttackers = team.attackers.find(c => c.id === card.id);
  const isInReserves = team.reserves.RES1?.id === card.id || team.reserves.RES2?.id === card.id || team.reserves.RES3?.id === card.id;
  
  // Determina la posizione corretta in base al ruolo del giocatore
  const getPositionForCard = () => {
    if (positionUpper === 'GK') return 'GK';
    if (positionUpper.startsWith('DEF')) return 'DEF';
    if (positionUpper.startsWith('MID')) return 'MID';
    if (positionUpper.startsWith('ATT')) return 'ATT';
    return position;
  };
  
  // Determina il colore del bordo in base a dove è selezionata
  const getBorderColor = () => {
    if (isInGoalkeeper) return 'ring-2 ring-blue-400 bg-blue-400/20';
    if (isInDefenders) return 'ring-2 ring-green-400 bg-green-400/20';
    if (isInMidfielders) return 'ring-2 ring-purple-400 bg-purple-400/20';
    if (isInAttackers) return 'ring-2 ring-red-400 bg-red-400/20';
    if (isInReserves) return 'ring-2 ring-yellow-400 bg-yellow-400/20';
    return '';
  };
  
  // Determina il badge per indicare dove è selezionata
  const getBadge = () => {
    if (isInGoalkeeper) return 'GK';
    if (isInDefenders) return 'DEF';
    if (isInMidfielders) return 'MID';
    if (isInAttackers) return 'ATT';
    if (team.reserves.RES1?.id === card.id) return 'RES1';
    if (team.reserves.RES2?.id === card.id) return 'RES2';
    if (team.reserves.RES3?.id === card.id) return 'RES3';
    return null;
  };
  
  const badge = getBadge();
  
  return (
    <button
      onClick={() => onSelect(card, getPositionForCard())}
      className={`football-card player-pick-card p-4 text-center transition-all relative ${getBorderColor()}`}
    >
      {badge && (
        <div className="absolute top-2 right-2 bg-yellow-400 text-black text-xs font-bold px-2 py-1 rounded z-10">
          {badge}
        </div>
      )}
      <div className="w-full h-32 bg-gradient-to-br from-[#4169E1] to-[#1E40AF] rounded-lg flex items-center justify-center mb-2">
        <div className="text-white text-sm font-bold">
          {card.player?.full_name || 'Player'}
        </div>
      </div>
      <div className="player-pick-card-meta mt-1.5 text-sm sm:text-base font-bold text-center leading-snug text-white">
        {card.rarity?.display_name || 'Basic'} · {position}
      </div>
    </button>
  );
};

export default TournamentEntry;

